// 7. 参加者リスト（管理者向け・S3 実API）。既存 Customer List 準拠：
// 一覧＝dataBox グリッド（行クリックで右詳細パネル選択）／編集＝パネル内で編集モード ON、
// 参加区分に応じ 3 パターン分岐（標準／不参加＋返金記録／未回答→参加）。バッジは status-badge。
// GET entries（全件＝集計用）＋ PATCH entries/:customer_id ＋ PATCH payments（一括）。
import { useEffect, useMemo, useState } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import StatusMessage from "../../../components/common/StatusMessage";
import {
  getRecitalEntries,
  patchRecitalEntry,
  cancelRecitalEntry,
  bulkRecitalPayments,
} from "../../../utils/recitalApi";
import { resolveFee } from "./recitalFee";
import { siblingCancelTargets } from "./cancelAlert";
import { buildEntryPatch, visibleSelection } from "./entryPatch";
import { memberStatusBadge } from "./memberStatus";
import "./recital.scss";

const RC_BREADCRUMBS = [
  { title: "Home", path: "/" },
  { title: "Recital", path: "/recital/2026/entries" },
  { title: "Entry List", path: "/recital/2026/entries" },
];

const TABS = [
  ["attend", "参加"],
  ["absent", "不参加"],
  ["pending", "未回答"],
];

const yen = (n) => `¥${n.toLocaleString()}`;

// 生年月日(ISO) → 表示用 YYYY/MM/DD ＋ 満年齢
const fmtBirthday = (iso) => (iso ? iso.replaceAll("-", "/") : "—");
const ageOf = (iso) => {
  if (!iso) return null;
  const b = new Date(iso);
  const n = new Date();
  let a = n.getFullYear() - b.getFullYear();
  const m = n.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && n.getDate() < b.getDate())) a -= 1;
  return a;
};
const birthdayLabel = (iso) =>
  iso ? `${fmtBirthday(iso)}（${ageOf(iso)}歳）` : "—";

// 参加可否 × 支払い の表示ルール（仕様 §1⑦）。c==="none" は対象外（バッジを出さず — 表示）
function payBadge(tab, payment) {
  if (tab === "attend")
    return payment === "paid" ? ["paid", "支払い済み"] : ["unpaid", "未払い"];
  if (tab === "absent") {
    if (payment === "paid") return ["check", "支払い済み（要確認）"];
    if (payment === "refunded") return ["refunded", "返金済み"];
    return ["none", "対象外 —"];
  }
  return ["none", "対象外 —"]; // pending
}

// 休会・退会の印（在籍は何も出さない）
function MemberStatusBadge({ status }) {
  const b = memberStatusBadge(status);
  return b ? <span className={`rc-badge ${b.cls}`}>{b.label}</span> : null;
}

// バッジ描画（none は淡色テキスト）
function PayCell({ tab, payment }) {
  const [c, label] = payBadge(tab, payment);
  if (c === "none") return <span className="rc-el-dash">対象外 —</span>;
  return <span className={`rc-badge ${c}`}>{label}</span>;
}

export default function RecitalEntryListPage() {
  const [list, setList] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [fees, setFees] = useState({ feeGeneral: 0, feeSibling: 0, feeYoung: 0 });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");
  const [tab, setTab] = useState("attend");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]); // 一括用チェック
  const [openId, setOpenId] = useState(null); // 右パネルで開いている会員
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false); // 申込取消の確認モーダル

  const notify = (text, type = "error") => {
    setMsgType(type);
    setMessage(text);
    setTimeout(() => setMessage(""), 3000);
  };

  // 初期ロード（rows＝全会員×申込状態・programs＝演目候補・event＝料金）
  useEffect(() => {
    let alive = true;
    getRecitalEntries()
      .then((d) => {
        if (!alive) return;
        setList(d.rows || []);
        setPrograms(d.programs || []);
        if (d.event) setFees(d.event);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setLoading(false);
        notify(e.message || "読み込みに失敗しました");
      });
    return () => {
      alive = false;
    };
  }, []);

  // 編集パネルの出演クラス候補をスタジオごとにグループ化（特別演目グループは API 側で命名済み）
  // 配役対象は dance のみ。MC/休憩など進行行（entry_type≠dance）は候補に出さない。
  const classGroups = useMemo(() => {
    const castable = programs.filter((p) => (p.entry_type || "dance") === "dance");
    const studios = [...new Set(castable.map((p) => p.studio))];
    return studios
      .map((s) => ({
        studio: s,
        items: castable
          .filter((p) => p.studio === s)
          .map((p) => ({ key: p.id, label: p.title })),
      }))
      .filter((g) => g.items.length > 0);
  }, [programs]);
  const programTitles = useMemo(
    () => Object.fromEntries(programs.map((p) => [p.id, p.title])),
    [programs],
  );
  const classKeyList = (keys) =>
    keys && keys.length
      ? keys.map((k) => programTitles[k]).filter(Boolean)
      : [];

  // 🟦3 料金区分：確定時スナップショット優先・未記録は 幼少→兄弟→一般（recitalFee.js）
  const youngIds = useMemo(
    () => new Set(programs.filter((p) => p.is_young).map((p) => p.id)),
    [programs],
  );
  const feeInfo = (row) => resolveFee(row, fees, youngIds);
  const FEE_CAT_LABEL = {
    general: fees.labelGeneral || "一般",
    sibling: fees.labelSibling || "兄弟",
    young: fees.labelYoung || "幼少",
  };

  const counts = useMemo(
    () => ({
      attend: list.filter((e) => e.tab === "attend").length,
      absent: list.filter((e) => e.tab === "absent").length,
      pending: list.filter((e) => e.tab === "pending").length,
      unpaid: list.filter((e) => e.tab === "attend" && e.payment === "unpaid")
        .length,
    }),
    [list],
  );

  const rows = list.filter(
    (e) =>
      e.tab === tab && (q === "" || e.name.includes(q) || e.kana.includes(q)),
  );
  const open = list.find((e) => e.id === openId) || null;

  const toggleSel = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  // 行クリック＝詳細パネルを開く／閉じる（閲覧モードで開く）
  const openRow = (e) => {
    if (openId === e.id) {
      closePanel();
      return;
    }
    setOpenId(e.id);
    setEditMode(false);
    setDraft(null);
  };
  const closePanel = () => {
    setOpenId(null);
    setEditMode(false);
    setDraft(null);
    setShowCancel(false);
  };

  // 一括：選択中エントリの支払いを更新（エントリ未作成＝未回答行はAPI側で対象外）
  const applyBulk = async (payment) => {
    if (busy) return;
    setBusy(true);
    try {
      // 画面に見えている選択行だけを送る（検索で隠れた行・返金済みはAPI側でも除外）
      const d = await bulkRecitalPayments(visibleSelection(selected, rows), payment);
      const updated = new Set(d.updated || []);
      setList((l) =>
        l.map((e) => (updated.has(e.id) ? { ...e, payment } : e)),
      );
      setSelected([]);
      notify(`${updated.size}件の支払いを更新しました`, "success");
    } catch (e) {
      notify(e.message || "一括更新に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  // 編集モード ON（ドラフト初期化）
  const startEdit = () => {
    setDraft({
      tab: open.tab,
      payment: open.payment,
      classKeys: [...(open.classKeys || [])],
      absentReason: open.absentReason || "",
    });
    setEditMode(true);
  };
  const cancelEdit = () => {
    setEditMode(false);
    setDraft(null);
  };

  const toggleDraftClass = (key) =>
    setDraft((d) => ({
      ...d,
      classKeys: d.classKeys.includes(key)
        ? d.classKeys.filter((k) => k !== key)
        : [...d.classKeys, key],
    }));

  // 元が支払い済み/返金済みなら不参加切替時に返金記録の対象
  const hadPayment =
    open && (open.payment === "paid" || open.payment === "refunded");

  // 保存：ドラフトを正規化して PATCH → 返却行で list を更新（パネルは開いたまま閲覧モードへ）
  const saveEdit = async () => {
    if (busy) return;
    // 参加なら出演クラスは1つ以上必須
    if (draft.tab === "attend" && (draft.classKeys?.length || 0) === 0) {
      notify("参加の場合は出演クラスを1つ以上選択してください");
      return;
    }
    setBusy(true);
    try {
      // 支払い状態は変えたときだけ送る（開きっぱなしの画面で他スタッフの更新を戻さない）
      const d = await patchRecitalEntry(open.id, buildEntryPatch(open, draft));
      // 単一行応答は世帯情報（siblingIds）を含まないため既存値を引き継ぐ（取消時の兄弟割引警告用）
      setList((l) => l.map((e) => (e.id === open.id ? { ...d.row, siblingIds: e.siblingIds } : e)));
      setSelected([]);
      setEditMode(false);
      setDraft(null);
      notify("保存しました", "success");
    } catch (e) {
      notify(e.message || "保存に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  // 取消：参加申込を取り消し（Entry 論理削除＝未回答へ。出演クラス削除・写真リセット・支払いは保持）
  const cancelEntry = async () => {
    if (busy || !open) return;
    const { id, name } = open;
    setBusy(true);
    try {
      const d = await cancelRecitalEntry(id);
      setList((l) => l.map((e) => (e.id === id ? { ...d.row, siblingIds: e.siblingIds } : e)));
      setSelected((s) => s.filter((x) => x !== id));
      setShowCancel(false);
      closePanel();
      notify(`${name} さんの参加申込を取り消しました`, "success");
    } catch (e) {
      notify(e.message || "取り消しに失敗しました");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rc-page rc-page-wide">
      <Breadcrumbs breadcrumbs={RC_BREADCRUMBS} />
      <div className="rc-bar">
        <h3>参加者リスト</h3>
      </div>
      {message && <StatusMessage message={message} type={msgType} />}

      <div className="rc-el">
        <div
          className="rc-el-sum"
          data-verify-counts={`${counts.attend}/${counts.absent}/${counts.pending}/${counts.unpaid}`}
        >
          <span>
            <b className="g">{counts.attend}</b>参加
          </span>
          <span className="sep">/</span>
          <span>
            <b className="y">{counts.absent}</b>不参加
          </span>
          <span className="sep">/</span>
          <span>
            <b className="m">{counts.pending}</b>未回答
          </span>
          <span className="sep">/</span>
          <span>
            <b className="r">{counts.unpaid}</b>未払い
          </span>
        </div>

        <div className="rc-el-filt">
          <div className="rc-el-tabs">
            {TABS.map(([k, label]) => (
              <button
                key={k}
                className={tab === k ? "on" : ""}
                onClick={() => {
                  setTab(k);
                  setSelected([]);
                }}
              >
                {label} ({counts[k]})
              </button>
            ))}
          </div>
          <div className="rc-el-search">
            <i className="fa-thin fa-magnifying-glass"></i>
            <input
              type="text"
              placeholder="氏名・フリガナで検索"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>

        {visibleSelection(selected, rows).length > 0 && (
          <div className="rc-el-bulk">
            <span className="lbl">{visibleSelection(selected, rows).length}件を選択中</span>
            <span className="sep">｜</span>支払いを
            <button
              className="rc-el-pill pri"
              disabled={busy}
              onClick={() => applyBulk("paid")}
            >
              支払い済みにする
            </button>
            <button
              className="rc-el-pill"
              disabled={busy}
              onClick={() => applyBulk("unpaid")}
            >
              未払いに戻す
            </button>
          </div>
        )}

        <div className={`rc-el-layout ${open ? "open" : ""}`}>
          <div className="rc-el-main">
            <div className="rc-data-wrap">
              <div className="rc-data" data-verify-tab={tab} data-verify-rows={rows.length}>
                <ul className="head">
                  <li className="chk c"></li>
                  <li>氏名</li>
                  <li>フリガナ</li>
                  <li>生年月日</li>
                  <li>スタジオ</li>
                  <li>出演クラス</li>
                  <li>支払い状況</li>
                  <li className="c"></li>
                </ul>
                {rows.length === 0 && (
                  <ul>
                    <li
                      style={{
                        gridColumn: "1 / -1",
                        justifyContent: "center",
                        color: "#8a8794",
                        padding: "18px",
                      }}
                    >
                      {loading ? "読み込んでいます…" : "該当する会員はいません"}
                    </li>
                  </ul>
                )}
                {rows.map((e) => {
                  const cls = classKeyList(e.tab === "attend" ? e.classKeys : []);
                  return (
                    <ul
                      key={e.id}
                      className={`row ${openId === e.id ? "sel" : ""}`}
                      onClick={() => openRow(e)}
                    >
                      <li
                        className="chk c"
                        onClick={(ev) => ev.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(e.id)}
                          onChange={() => toggleSel(e.id)}
                        />
                      </li>
                      <li>
                        {e.name}
                        <MemberStatusBadge status={e.memberStatus} />
                      </li>
                      <li>{e.kana}</li>
                      <li>{birthdayLabel(e.birthday)}</li>
                      <li>{e.studio}</li>
                      <li>
                        {cls.length ? (
                          <>
                            {cls[0]}
                            {cls.length > 1 && (
                              <span className="rc-el-more">
                                　他{cls.length - 1}件
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="rc-el-dash">—</span>
                        )}
                      </li>
                      <li>
                        <PayCell tab={e.tab} payment={e.payment} />
                      </li>
                      <li className="c chev">›</li>
                    </ul>
                  );
                })}
              </div>
            </div>
          </div>

          {open && (
            <aside className={`rc-el-side ${editMode ? "editing" : ""}`}>
              <div className="sh">
                <p>
                  {open.name}
                  <MemberStatusBadge status={open.memberStatus} />
                  {editMode && <span className="rc-badge edit">編集中</span>}
                </p>
                <div className="acts">
                  {!editMode ? (
                    <button className="ebtn" onClick={startEdit}>
                      編集
                    </button>
                  ) : (
                    <>
                      <button className="ebtn" onClick={cancelEdit}>
                        キャンセル
                      </button>
                      <button
                        className="ebtn primary"
                        onClick={saveEdit}
                        disabled={draft.tab === "pending" || busy}
                      >
                        {busy ? "保存中…" : "保存"}
                      </button>
                    </>
                  )}
                  <button
                    className="ebtn x"
                    onClick={closePanel}
                    aria-label="閉じる"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="sb">
                {!editMode ? (
                  /* ===== 閲覧モード ===== */
                  <>
                    <dl>
                      <dt>フリガナ</dt>
                      <dd>{open.kana}</dd>
                    </dl>
                    <dl>
                      <dt>生年月日</dt>
                      <dd>{birthdayLabel(open.birthday)}</dd>
                    </dl>
                    <dl>
                      <dt>スタジオ</dt>
                      <dd>{open.studio}</dd>
                    </dl>
                    <dl>
                      <dt>支払い</dt>
                      <dd>
                        <PayCell tab={open.tab} payment={open.payment} />
                      </dd>
                    </dl>
                    {open.tab === "attend" && (
                      <dl>
                        <dt>料金区分</dt>
                        <dd>
                          {FEE_CAT_LABEL[feeInfo(open).category]} ・{" "}
                          {yen(feeInfo(open).amount)}
                        </dd>
                      </dl>
                    )}
                    <dl>
                      <dt>出演クラス</dt>
                      <dd>
                        {open.tab === "attend" &&
                        classKeyList(open.classKeys).length
                          ? classKeyList(open.classKeys).join(" / ")
                          : "—"}
                      </dd>
                    </dl>
                    {open.tab === "absent" && (
                      <dl>
                        <dt>不参加理由</dt>
                        <dd>{open.absentReason || "—"}</dd>
                      </dl>
                    )}
                    {open.tab !== "pending" && (
                      <button
                        className="rc-el-cancel"
                        onClick={() => setShowCancel(true)}
                      >
                        申込を取消
                      </button>
                    )}
                  </>
                ) : (
                  /* ===== 編集モード（3 パターン分岐） ===== */
                  <>
                    <div className="sectl" style={{ marginTop: 0 }}>
                      参加区分
                    </div>
                    <div className="seg">
                      <button
                        className={draft.tab === "attend" ? "on" : ""}
                        onClick={() => setDraft((d) => ({ ...d, tab: "attend" }))}
                      >
                        参加
                      </button>
                      <button
                        className={draft.tab === "absent" ? "on no" : ""}
                        onClick={() => setDraft((d) => ({ ...d, tab: "absent" }))}
                      >
                        不参加
                      </button>
                    </div>

                    {draft.tab === "pending" && (
                      <div className="hint">
                        「参加」か「不参加」を選択してください。
                      </div>
                    )}

                    {draft.tab === "attend" && (
                      <>
                        <div className="sectl">支払い状況</div>
                        <div className="seg">
                          <button
                            className={draft.payment === "paid" ? "on" : ""}
                            onClick={() =>
                              setDraft((d) => ({ ...d, payment: "paid" }))
                            }
                          >
                            支払い済み
                          </button>
                          <button
                            className={draft.payment !== "paid" ? "on" : ""}
                            onClick={() =>
                              setDraft((d) => ({ ...d, payment: "unpaid" }))
                            }
                          >
                            未払い
                          </button>
                        </div>
                        <div className="sectl">出演クラス（複数可）</div>
                        <div className="rc-classscroll">
                          {classGroups.map((g) => {
                            const sel = g.items.filter((o) =>
                              draft.classKeys.includes(o.key),
                            ).length;
                            return (
                              <div key={g.studio}>
                                <div className="rc-csub">
                                  <span className="nm">{g.studio}</span>
                                  <span className={`cnt${sel > 0 ? " has" : ""}`}>
                                    {sel}
                                  </span>
                                </div>
                                <div className="classpick">
                                  {g.items.map((o) => {
                                    const on = draft.classKeys.includes(o.key);
                                    return (
                                      <label key={o.key} className={on ? "on" : ""}>
                                        <input
                                          type="checkbox"
                                          checked={on}
                                          onChange={() => toggleDraftClass(o.key)}
                                        />
                                        {o.label}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {draft.tab === "absent" && (
                      <>
                        <div className="sectl">不参加の理由（任意）</div>
                        <textarea
                          placeholder="差し支えなければご記入ください"
                          value={draft.absentReason}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              absentReason: e.target.value,
                            }))
                          }
                        />
                        <div className="sectl">支払い・返金</div>
                        {hadPayment ? (
                          <>
                            <div className="note">
                              支払い済み（{FEE_CAT_LABEL[feeInfo(open).category]}・
                              {yen(feeInfo(open).amount)}
                              ）です。返金するかは規約・方針に従ってご判断ください。
                            </div>
                            <div className="seg" style={{ marginTop: 8 }}>
                              <button
                                className={
                                  draft.payment !== "refunded" ? "on" : ""
                                }
                                onClick={() =>
                                  setDraft((d) => ({ ...d, payment: "paid" }))
                                }
                              >
                                返金なし
                              </button>
                              <button
                                className={
                                  draft.payment === "refunded" ? "on" : ""
                                }
                                onClick={() =>
                                  setDraft((d) => ({
                                    ...d,
                                    payment: "refunded",
                                  }))
                                }
                              >
                                返金済み
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="note muted">
                            未払いのため支払い対象外です（返金の記録はありません）。
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* 申込取消の確認モーダル（破壊的操作のため明示確認） */}
      {showCancel && open && (
        <div
          className="rc-modal-ov"
          onClick={() => !busy && setShowCancel(false)}
        >
          <div className="rc-confirm" onClick={(e) => e.stopPropagation()}>
            <p className="ttl">{open.name} さんの参加申込を取り消しますか？</p>
            <p className="sub">取り消すと、この会員は「未回答」に戻ります。</p>
            {siblingCancelTargets(open, list).length > 0 && (
              <div className="warn" data-verify-sibling-alert>
                <p>
                  【注意】
                  {open.feeCategory === "sibling"
                    ? `${open.name} さんは兄弟割引で申し込んでいます。`
                    : `${open.name} さんと同じ世帯に、兄弟割引で申し込んでいる方がいます。`}
                </p>
                <ul>
                  {siblingCancelTargets(open, list).map((r) => (
                    <li key={r.id}>
                      {r.name} さん（{FEE_CAT_LABEL.sibling} {yen(Number(r.feeAmount) || 0)}・
                      {payBadge(r.tab, r.payment)[1]}）
                    </li>
                  ))}
                </ul>
                <p>
                  上記の方の料金は、申込時のまま変わりません。差額の対応が必要な場合は、個別にご対応ください。
                </p>
              </div>
            )}
            <div className="acts">
              <button
                className="ebtn"
                onClick={() => setShowCancel(false)}
                disabled={busy}
              >
                キャンセル
              </button>
              <button className="ebtn danger" onClick={cancelEntry} disabled={busy}>
                {busy ? "取り消し中…" : "申込を取消"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
